/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-empty */
import cytoscape from 'cytoscape';
import contextMenus from 'cytoscape-context-menus';
import 'cytoscape-context-menus/cytoscape-context-menus.css';
import { run as runActions } from '../../../store/workspaces/workspaces/run';
import store from '../../../store/store';
import { callFunctions } from '../../../api/functions/call.functions';
import request from 'src/request/request';

cytoscape.use(contextMenus);


type Cy = cytoscape.Core;
type CyTarget = cytoscape.SingularElementReturnValue | cytoscape.Core;

interface ContextMenuClickEvent {
  target?: CyTarget;
  position?: cytoscape.Position;
}

interface ContextMenuItem {
  id: string;
  content: string;
  selector?: string;
  tooltipText?: string;
  show?: boolean;
  disabled?: boolean;
  hasTrailingDivider?: boolean;
  coreAsWell?: boolean;
  submenu?: ContextMenuItem[];
  onClickFunction?: (evt: ContextMenuClickEvent) => void | Promise<void>;
}

interface ContextMenusOptions {
  evtType?: 'cxttap' | 'taphold' | string;
  menuItems: ContextMenuItem[];
}

interface ContextMenusInstance {
  isActive: () => boolean;
  appendMenuItem: (item: ContextMenuItem) => void;
  appendMenuItems: (items: ContextMenuItem[]) => void;
  removeMenuItem: (id: string) => void;
  destroy: () => void;
}

interface ContextEventDef { type: string; key: string }

interface ContextElementDef {
  key: string;
  type?: 'menu' | 'button';
  label?: string;
  contentHtml?: string; 
  iconClass?: string; 
  icon?: { name?: string; code?: string; image?: string };
  selector?: 'node' | 'edge' | 'background' | string; 
  class?: string; 
  tooltipText?: string;
  show?: boolean;
  disabled?: boolean;
  hasTrailingDivider?: boolean;
  coreAsWell?: boolean;
  submenu?: ContextElementDef[]; 
  elements?: ContextElementDef[]; 
  events?: ContextEventDef[]; 
}
interface ContextData { elements: ContextElementDef[] }

type AddNodesFn = (nodes: cytoscape.ElementDefinition[], position?: cytoscape.Position) => void | Promise<void>;
type RemoveNodeFn = (node: cytoscape.SingularElementReturnValue) => void | Promise<void>;
type TranslateFn = (key: string) => string;


export const initGraphEditContextMenu = (
  cy: Cy,
  contextData: ContextData,
  addNodesCytoscape: AddNodesFn,
  removeNode: RemoveNodeFn,
  translate: TranslateFn,
): ContextMenusInstance => {
  const parseClassConfig = (config?: string): Partial<ContextMenuItem> & { selector?: string; coreAsWell?: boolean } => {
    const cfg: Partial<ContextMenuItem> & { selector?: string; coreAsWell?: boolean } = {};
    if (!config) return cfg;
    const lower = config.toLowerCase();
    const tokens = lower.split(/\s+/g).filter(Boolean);
    
    if (tokens.includes('background') || tokens.includes('core')) cfg.coreAsWell = true;
    if (tokens.includes('node')) cfg.selector = 'node';
    if (tokens.includes('edge')) cfg.selector = 'edge';
    if (tokens.includes('divider') || tokens.includes('trailing-divider') || tokens.includes('separator')) cfg.hasTrailingDivider = true;
    if (tokens.includes('disabled') || tokens.includes('is-disabled')) cfg.disabled = true;
    if (tokens.includes('hidden') || tokens.includes('is-hidden')) cfg.show = false;
    const m = lower.match(/selector[:=]([^\s]+)/);
    if (m && m[1]) cfg.selector = m[1];
    return cfg;
  };

  const makeClickHandler = (ctxKey: string, events?: ContextEventDef[]) => async (evt: ContextMenuClickEvent) => {
    const target = evt?.target as cytoscape.SingularElementReturnValue | undefined;
    const eventKey = events?.find((e) => e.type === 'onClick')?.key;

    if (!eventKey) {
      console.warn('Context menu: missing eventKey for item', ctxKey);
      return;
    }

    let selectedElements: Record<string, unknown>[] = [];
    try {
      const raw = localStorage.getItem('selectedElements');
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) selectedElements = parsed;
    } catch {
      // Ignore JSON parse errors and fallback to empty array
    }

    const data = selectedElements.length ? selectedElements : target && typeof (target as cytoscape.SingularElementReturnValue).data === 'function' ? (target as cytoscape.SingularElementReturnValue).data() : undefined;

    try {
      const idVal = target && typeof (target as cytoscape.SingularElementReturnValue).data === 'function' ? (target as cytoscape.SingularElementReturnValue).data()?.id : undefined;
      const nodeId = typeof idVal === 'number' ? idVal : idVal != null ? Number(idVal) : undefined;
      const state = store.getState();
      const activeData = state?.workspaces?.instances?.find((i) => i?.isActive)?.data;
      await runActions(Number(eventKey), nodeId, { data });

      if (!activeData) {
        console.warn('Context menu: active workspace data not found');
        return;
      }
      callFunctions(activeData.functions, { addNodesCytoscape, removeNode, itemPosition: evt?.position },false,);
    } catch (error) {
      console.error('Graph context menu error:', error);
    }
  };

  const toMenuItem = (def: ContextElementDef): ContextMenuItem => {
    const translated = def.label ? (translate ? translate(def.label) : def.label) : '';
    const derivedIconClass = def.iconClass || (def.icon?.name ? `e-icons ${def.icon.name}` : undefined);
    const baseInner = def.contentHtml ? def.contentHtml : derivedIconClass ? `<span class="${derivedIconClass}"></span> ${translated}` : translated;
    const content = def.class ? `<span class="${def.class}">${baseInner}</span>` : baseInner;
    const isBackground = def.selector === 'background';
    const classCfg = parseClassConfig(def.class);
    const selector = isBackground ? undefined : (def.selector ?? classCfg.selector ?? 'node');
    const coreAsWell = def.coreAsWell ?? (isBackground || !!classCfg.coreAsWell);

    return {
      id: def.key,
      content,
      selector,
      coreAsWell,
      tooltipText: def.tooltipText,
      show: def.show ?? (classCfg.show ?? true),
      disabled: def.disabled ?? (classCfg.disabled ?? false),
      hasTrailingDivider: def.hasTrailingDivider ?? (classCfg.hasTrailingDivider as boolean | undefined),
      onClickFunction: makeClickHandler(def.key, def.events),
      submenu: (def.submenu ?? def.elements)?.filter((x) => !!x)?.map(toMenuItem),
    };
  };

  const tr = (k: string, fallback: string) => (translate ? translate(k) : '') || fallback;

  const uniqueId = (prefix = 'n') =>
    `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

  const addNodeWithEdge = async (evt: ContextMenuClickEvent) => {


    // TODO:
    // 1) Dynamischen Endpoint aufrufen:
    //    await request(true, 'POST', 'services/graph/{path}', { ... });
    //
    // 2) Response auswerten und Knoten in Cytoscape hinzufügen:
    //    addNodesCytoscape([data], newPos);
    //
    //    Entsprechend Aufruf Zeile 196.


    const source = evt?.target as cytoscape.SingularElementReturnValue | undefined;
    if (!source || !source.isNode?.()) return;

    const label = window.prompt(tr('graph.add_node.label', 'Name des neuen Knotens:'), '');
    if (label == null || label.trim() === '') return;

    const edgeLabel = window.prompt(tr('graph.add_edge.label', 'Bezeichnung der Kante:'), '');

    const srcPos = source.position?.() || evt?.position || { x: 0, y: 0 };
    const newPos = { x: srcPos.x + 120, y: srcPos.y + 40 };
    const newNodeId = uniqueId('n');
    const newNode: cytoscape.ElementDefinition = {
      group: 'nodes',
      data: { id: newNodeId, label },
      position: newPos,
    };

    try {
      await addNodesCytoscape([newNode], newPos);
    } catch {
      // Fallback: direkt in cy hinzufügen
      (cy as any).add(newNode);
    }

    const edgeId = uniqueId('e');
    (cy as any).add({
      group: 'edges',
      data: { id: edgeId, source: (source as any).id(), target: newNodeId, label: edgeLabel },
    });
    console.log('ADD_NODE_EDGE_DONE', { sourceId: (source as any).id(), targetId: newNodeId, edgeLabel, nodeLabel: label });

    try {
      await request(true, 'POST', 'services/graph/add-node-edge', {
        sourceId: (source as any).id(),
        node: { id: newNodeId, label },
        edge: { id: edgeId, label: edgeLabel },
      });
    } catch {}
  };

  const startConnectMode = async (evt: ContextMenuClickEvent) => {
    const source = evt?.target as cytoscape.SingularElementReturnValue | undefined;
    if (!source || !source.isNode?.()) return;

    const container = (cy as any).container() as HTMLElement;
    const prevCursor = container.style.cursor;
    container.style.cursor = 'crosshair';

    const finish = () => {
      container.style.cursor = prevCursor;
      (cy as any).off('tap', onTapAny);
      document.removeEventListener('keydown', onKey);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
    };
    document.addEventListener('keydown', onKey);

    const onTapAny = async (e: any) => {
      const tgt = e.target;
      if (tgt && tgt.isNode?.() && tgt.id() !== (source as any).id()) {
        const edgeLabel = window.prompt(tr('graph.add_edge.label', 'Bezeichnung der Kante:'), '');
        const edgeId = uniqueId('e');
        (cy as any).add({
          group: 'edges',
          data: { id: edgeId, source: (source as any).id(), target: tgt.id(), label: edgeLabel, type: 'manual', lineColor: '#999', targetArrowColor: '#999', targetArrowShape: 'triangle' },
        });
        console.log('CONNECT_DONE', { sourceId: (source as any).id(), targetId: tgt.id(), label: edgeLabel });
        try {
          await request(true, 'POST', 'services/graph/connect', {
            sourceId: (source as any).id(),
            targetId: tgt.id(),
            edge: { id: edgeId, label: edgeLabel },
          });
        } catch {}
      }
      finish();
    };
    (cy as any).one('tap', onTapAny);
  };

  const renameNode = async (evt: ContextMenuClickEvent) => {
    const node = evt?.target as cytoscape.SingularElementReturnValue | undefined;
    if (!node || !node.isNode?.()) return;

    const cur = node.data('label') ?? node.data('name') ?? '';
    const next = window.prompt(tr('graph.rename.label', 'Neuer Name:'), cur);
    if (next == null || next === cur) return;

    node.data({ ...node.data(), label: next, name: next });
    console.log('RENAME_NODE_DONE', { nodeId: (node as any).id(), from: cur, to: next });

    try {
      await request(true, 'POST', 'services/graph/rename-node', {
        id: (node as any).id(),
        label: next,
      });
    } catch {}
  };

  const renameEdge = async (evt: ContextMenuClickEvent) => {
    const edge = evt?.target as cytoscape.SingularElementReturnValue | undefined;
    if (!edge || !edge.isEdge?.()) return;
    const cur = edge.data('label') ?? edge.data('name') ?? '';
    const next = window.prompt(tr('graph.rename_edge.label', 'Neue Bezeichnung:'), cur);
    if (next == null || next === cur) return;
    edge.data({ ...edge.data(), label: next, name: next });
    console.log('RENAME_EDGE_DONE', { edgeId: (edge as any).id(), sourceId: edge.data('source'), targetId: edge.data('target'), from: cur, to: next });

    try {
      await request(true, 'POST', 'graph/rename-edge', {
        sourceId: edge.data('source'),
        targetId: edge.data('target'),
        fromLabel: cur,
        toLabel: next,
        cid: (edge as any).id?.() ?? (edge as any).id(),
      });
    } catch {}
  };

  const deleteNodeOnly = async (evt: ContextMenuClickEvent) => {
    const node = evt?.target as cytoscape.SingularElementReturnValue | undefined;
    if (!node || !node.isNode?.()) return;
    const nodeId = (node as any).id?.() ?? (node as any).id();
    const nodeLabel = (node as any).data?.('label');
    const edges = (node as any).connectedEdges?.().map((e: any) => ({ edgeId: e.id(), sourceId: e.data('source'), targetId: e.data('target'), label: e.data('label') })) || [];
    try {
      await removeNode(node);
      console.log('DELETE_NODE_DONE', { nodeId, nodeLabel, edges });
    } catch {
      node.remove(); 
      console.log('DELETE_NODE_DONE', { nodeId, nodeLabel, edges });
    }
    try {
      await request(true, 'POST', 'services/graph/delete-node', { id: nodeId });
    } catch {}
  };


  const deleteEdge = async (evt: ContextMenuClickEvent) => {
    const edge = evt?.target as cytoscape.SingularElementReturnValue | undefined;
    if (!edge || !edge.isEdge?.()) return;
    const info = { edgeId: (edge as any).id(), sourceId: edge.data('source'), targetId: edge.data('target'), label: edge.data('label') };
    edge.remove();
    console.log('DELETE_EDGE_DONE', info);
    try {
      await request(true, 'POST', 'services/graph/delete-edge', { sourceId: info.sourceId, targetId: info.targetId, label: info.label, cid: info.edgeId });
    } catch {}
  };

  const backendItems: ContextMenuItem[] = (contextData?.elements || []).map(toMenuItem);

  const localEditItems: ContextMenuItem[] = [
    {
      id: 'local:add-node-edge',
      content: '➕ ' + tr('Knoten hinzufügen', 'Knoten + Kante hinzufügen'),
      selector: 'node',
      onClickFunction: addNodeWithEdge,
    },
    {
      id: 'local:connect',
      content: '🔗 ' + tr('Verbinden…', 'Verbinden…'),
      selector: 'node',
      onClickFunction: startConnectMode,
    },
    {
      id: 'local:rename',
      content: '✏️ ' + tr('Umbenennen…', 'Umbenennen…'),
      selector: 'node',
      onClickFunction: renameNode,
    },
    {
      id: 'local:rename-edge',
      content: '✏️ ' + tr('Beziehung umbenennen…', 'Beziehung umbenennen…'),
      selector: 'edge',
      onClickFunction: renameEdge,
    },
    {
      id: 'local:delete-node',
      content: '🗑️ ' + tr('Knoten löschen', 'Knoten löschen'),
      selector: 'node',
      onClickFunction: deleteNodeOnly,
    },
    {
      id: 'local:delete-edge',
      content: '🗑️ ' + tr('Beziehung löschen', 'Beziehung löschen'),
      selector: 'edge',
      onClickFunction: deleteEdge,
    },
  ];

  const menuItems: ContextMenuItem[] = [...backendItems, ...localEditItems];

  const options: ContextMenusOptions = {
    evtType: 'cxttap',
    menuItems,
  };

  const existing = (cy as Cy & { contextMenus?: (arg?: string | ContextMenusOptions) => ContextMenusInstance | undefined }).contextMenus ? (cy as Cy & { contextMenus?: (arg?: string | ContextMenusOptions) => ContextMenusInstance | undefined }).contextMenus('get') as ContextMenusInstance | undefined : undefined;
  try { existing?.destroy?.(); } catch {}
  const cyWithContextMenus = cy as Cy & { contextMenus: (options: ContextMenusOptions) => ContextMenusInstance };
  const instance = cyWithContextMenus.contextMenus(options);
  return instance;
};

export const contextMenu = initGraphEditContextMenu;
